// License model. Derives a display/gating status from the signed-in user's license, matching
// the published plans: Free (personal/non-commercial), Trial (14-day), Business/Enterprise
// (commercial). The Free/paid split is a usage-rights distinction, so the core add-in stays
// usable on Free — paid unlocks commercial use, teams, and support.
import { computed } from 'vue'
import { auth } from './auth'
import { config } from './config'
import type { LicenseInfo } from '../types/auth'

export interface LicenseStatus {
  tier: string // display name, e.g. "Free", "Trial", "Business"
  label: string // badge text
  isValid: boolean // an active, non-expired license of any tier
  isTrial: boolean
  daysLeft: number | null
  commercialAllowed: boolean // may be used for work/organisation
  needsUpgrade: boolean // show the upgrade CTA
}

const PAID = new Set(['professional', 'teams', 'business', 'enterprise'])

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isFinite(ms) ? Math.max(0, Math.ceil(ms / 86_400_000)) : null
}

function isExpired(iso?: string): boolean {
  if (!iso) return false
  const t = new Date(iso).getTime()
  return Number.isFinite(t) ? t < Date.now() : false
}

export function deriveLicense(info: LicenseInfo | null | undefined): LicenseStatus {
  if (!info || !info.type || info.type.toLowerCase() === 'none') {
    return {
      tier: 'None',
      label: 'No licence',
      isValid: false,
      isTrial: false,
      daysLeft: null,
      commercialAllowed: false,
      needsUpgrade: true,
    }
  }
  const type = info.type.toLowerCase()
  const isTrial = type === 'trial'
  const active = info.isActive !== false && !isExpired(info.expiresAt)
  const days = daysUntil(info.expiresAt)
  const paidActive = PAID.has(type) && active

  return {
    tier: capitalize(info.type),
    label: isTrial ? (days != null ? `Trial · ${days}d left` : 'Trial') : capitalize(info.type),
    isValid: active,
    isTrial,
    daysLeft: isTrial ? days : null,
    commercialAllowed: paidActive || (isTrial && active),
    // Free or expired → suggest upgrading for commercial use.
    needsUpgrade: !paidActive && !(isTrial && active),
  }
}

/** Reactive license status for the current session. */
export const license = computed<LicenseStatus>(() => deriveLicense(auth.user?.license))

/**
 * Open the web billing portal (plan selection + Stripe checkout / management) so the user can
 * upgrade or manage their subscription. Routing to the existing portal avoids needing a Firebase
 * token inside the add-in; the license re-validates on the next session/refresh.
 */
export function openUpgrade(): void {
  const url = config.billingUrl
  try {
    if (typeof Office !== 'undefined' && Office.context?.ui?.displayDialogAsync) {
      Office.context.ui.displayDialogAsync(url, { height: 70, width: 50 })
      return
    }
  } catch {
    /* fall through to window.open */
  }
  window.open(url, '_blank', 'noopener')
}
